import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import * as Sentry from '@sentry/angular';
import { AuthService } from 'auth';
import { NzMessageService } from 'ng-zorro-antd';
import { SettingsService } from 'settings';
import { ISession } from 'trading';
import { WindowManagerService } from 'window-manager';
import { Workspace, WorkspacesManager } from 'workspace-manager';

import {
  AppHealthLogEntry,
  AppHealthLogger,
} from '../../../../projects/performance-tools/src/lib/app-health-logs/app-health-logger';
import { PerformanceMetricsService } from '../../../../projects/performance-tools/src/lib/performance-metrics.service';
import { SessionsService } from '../../../../projects/real-trading/src/trading/services/sessions.service';
import { PersistentTradeLogger } from '../../services/PersistentTradeLogger';
import { TradeLogEntry } from '../../services/TradeLogger';

@Component({
  selector: 'app-user-feedback',
  templateUrl: './user-feedback.component.html',
  styleUrls: ['./user-feedback.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserFeedbackComponent implements OnInit {
  @Output() onClose: EventEmitter<void> = new EventEmitter();

  form: FormGroup;
  isFormSubmitted: boolean = false;
  sessions: ISession[];

  constructor(
    private _authService: AuthService,
    private _settingsService: SettingsService,
    private _messageService: NzMessageService,
    private metricsService: PerformanceMetricsService,
    private healthLoggerService: AppHealthLogger,
    private tradeLogger: PersistentTradeLogger,
    private sessionsService: SessionsService,
    private _windowManagerService: WindowManagerService,
    private _workspacesManager: WorkspacesManager,
  ) {}

  ngOnInit(): void {
    this.form = new FormGroup({
      message: new FormControl('', [Validators.required]),
      consentToAttachTradeLog: new FormControl(false),
    });
    this.sessionsService.getSessions().subscribe((sessions: ISession[]) => {
      this.sessions = sessions;
    });
    this._windowManagerService.blurActiveWindow();
  }

  submit(): void {
    const eventId: string = new Date().getTime() + '';
    const eventName: string = `User Feedback (ID: ${eventId})`;
    const email: string = this._authService.userInfo.name;
    const message: string = `${eventName}:\n\n${this.form.get('message').value}`;
    const sentryScope: Sentry.Scope = Sentry.getCurrentScope();
    let sentryEventId: string;

    this.isFormSubmitted = true;

    if (this.form.get('message').hasError('required')) {
      return;
    }

    sentryScope.setFingerprint([eventName]);
    sentryScope.addAttachment({
      filename: `settings_${eventId}.json`,
      data: JSON.stringify(this._settingsService.settings.value),
    });

    if (this.sessions) {
      sentryScope.addAttachment({
        filename: `trading_sessions_${eventId}.json`,
        data: JSON.stringify(this.sessions),
      });
    }
    try {
      // Set tag for active workspace
      const activeWorkspace: Workspace =
        this._workspacesManager.getActiveWorkspace();
      sentryScope.setTag('activeWorkspace', activeWorkspace?.name);
    } catch (e) {
      console.error(e);
    }

    try {
      if (this.metricsService) {
        const metrics: { [key: string]: number } =
          this.metricsService.getAllMetrics();
        sentryScope.addAttachment({
          filename: `metrics_${eventId}.json`,
          data: JSON.stringify(metrics),
        });
      }
    } catch (e) {
      console.error(e);
    }

    try {
      if (this.healthLoggerService) {
        const logs: AppHealthLogEntry[] = this.healthLoggerService.getAllLogs();
        sentryScope.addAttachment({
          filename: `health_logs_${eventId}.json`,
          data: JSON.stringify(logs),
        });
      }
    } catch (e) {
      console.error(e);
    }

    try {
      const hasConsent: boolean = this.form.get(
        'consentToAttachTradeLog',
      ).value;

      if (this.tradeLogger && hasConsent) {
        const logs: TradeLogEntry[] = this.tradeLogger.getAllLogs();
        sentryScope.addAttachment({
          filename: `trade_logs_${eventId}.json`,
          data: JSON.stringify(logs),
        });
      }
    } catch (e) {
      console.error(e);
    }

    sentryEventId = Sentry.captureMessage(eventName, {
      tags: {
        email,
      },
    });
    Sentry.captureUserFeedback({
      event_id: sentryEventId,
      name: email,
      email,
      comments: message,
    });
    sentryScope.clearAttachments();
    sentryScope.clear();

    this.onClose.emit();
    this._windowManagerService.focusActiveWindow();
    this._messageService.success('Your feedback has been sent');
  }

  close(event: Event): void {
    event.preventDefault();
    this.onClose.emit();
    this._windowManagerService.focusActiveWindow();
  }
}
